import React, { useEffect, useRef, useState } from "react"
import ArrowBackIcon from "@mui/icons-material/ArrowBack"
import "./SerieMobile.css"
import CosmosBackground from "../CosmosBackground"
import { useNavigate, useParams } from "react-router-dom"
import ReactPlayer from "react-player"
import requests from "../../api/requests"
import KeyboardArrowDownIcon from "@mui/icons-material/KeyboardArrowDown"
import KeyboardArrowUpIcon from "@mui/icons-material/KeyboardArrowUp"
import PictureAsPdfIcon from "@mui/icons-material/PictureAsPdf"
import { Skeleton } from "@mui/material"
import { handleAPIError } from "../../api/error"
import { errorSnackBar } from "../snackBarOptions"
import { useSnackbar } from "react-simple-snackbar"
import { useDispatch } from "react-redux"
import { usePlayerPreferences } from "../../utils/usePlayerPreferences"

const baseURL = process.env.REACT_APP_CMS_URL

function SerieMobile() {
  const [lastPlayedValue, setLastPlayedValue] = useState(0.0)
  const [lastEpisode, setLastEpisode] = useState(null)
  const [actualEpisode, setActualEpisode] = useState(null)
  const [hasChangedEpisode, setHasChangedEpisode] = useState(false)
  const [retryKey, setRetryKey] = useState(0)
  const retryCountRef = useRef(0)
  const retryTimerRef = useRef(null)

  const playerRef = useRef()

  const [content, setContent] = useState({
    episodes: [],
  })
  const [meta, setMeta] = useState({
    watched: false,
    percentage_watched: 0.0,
    serie_percentage_watched: 0.0,
  })

  const [isLoading, setIsLoading] = useState(true)
  const { id } = useParams()

  const getEpisodeIndex = (id) => {
    return content.episodes.findIndex((episode) => episode.id === id)
  }

  const [openErrorSnackbar] = useSnackbar(errorSnackBar)
  const navigate = useNavigate()
  const dispatch = useDispatch()

  const { onPlayerReady } = usePlayerPreferences(playerRef)

  useEffect(() => {
    retryCountRef.current = 0

    const fetchEpisode = async () => {
      try {
        const response = await requests.getSeries(id)
        const episodes = response.data?.serie?.episodes ?? []
        const meta = response.data?.meta

        const initialEpisode =
          !meta?.watched && meta?.episode_id
            ? (episodes.find((ep) => ep.id === meta.episode_id) ?? episodes[0])
            : episodes[0]

        setContent(response.data?.serie)
        setMeta(meta)
        setActualEpisode(initialEpisode)
        setLastEpisode(episodes[episodes.length - 1])
        setRetryKey(0)
      } catch (error) {
        handleAPIError(error, { dispatch, navigate, openErrorSnackbar })
      } finally {
        setIsLoading(false)
      }
    }

    fetchEpisode()
  }, [id, dispatch, navigate])

  useEffect(() => {
    return () => clearTimeout(retryTimerRef.current)
  }, [])

  const onError = () => {
    if (retryCountRef.current >= 2) return
    retryTimerRef.current = setTimeout(() => {
      retryCountRef.current += 1
      setRetryKey((k) => k + 1)
    }, 2000)
  }

  const onDuration = () => {
    if (meta.watched) return

    if (!hasChangedEpisode) {
      playerRef.current.seekTo(meta?.percentage_watched || 0.0, "fraction")
    }
  }

  const [showFullDescription, setShowFullDescription] = useState(false)
  const [hasShortDescription, setHasShortDescription] = useState(false)

  const description = actualEpisode?.description?.length > 0
    ? actualEpisode.description
    : (content?.description || "")

  useEffect(() => {
    const desc = actualEpisode?.description?.length > 0
      ? actualEpisode.description
      : (content?.description || "")
    setShowFullDescription(desc.length < 300)
    setHasShortDescription(false)
  }, [actualEpisode?.description, content?.description])

  const limitedDescription = showFullDescription
    ? description
    : description.substring(0, 300) + "..."

  const saveWatchedProgress = async (played, watch) => {
    const totalEpisodes = content?.episodes?.length
    const actualEpisodeIndex = getEpisodeIndex(actualEpisode?.id)
    const seriePercentageWatched = (actualEpisodeIndex + 1) / totalEpisodes
    try {
      await requests.setEpisodeWatched(actualEpisode?.id, {
        serie_id: content?.id,
        watched: watch,
        percentage_watched: played,
        serie_percentage_watched: seriePercentageWatched,
      })
    } catch (error) {
      console.log(error)
    }
  }

  const onProgress = (state) => {
    if (state.played > lastPlayedValue + 0.1) {
      if (state.played > 0.8 && actualEpisode?.id === lastEpisode?.id) {
        saveWatchedProgress(state.played, true)
        return
      }
      saveWatchedProgress(state.played, false)
      setLastPlayedValue(state.played)
    }
  }

  const onEnded = (state) => {
    if (actualEpisode?.id === lastEpisode?.id) {
      saveWatchedProgress(1, true)
      setLastPlayedValue(1)
      return
    }

    const nextEpisodeIndex = getEpisodeIndex(actualEpisode?.id) + 1
    const nextEpisode = content?.episodes[nextEpisodeIndex]
    retryCountRef.current = 0
    setRetryKey(0)
    setHasChangedEpisode(true)
    setActualEpisode(nextEpisode)
    playerRef.current.seekTo(0.0, "fraction")
    setLastPlayedValue(0.0)
  }

  const openAttachment = (url) => {
    window.open(`${baseURL}${url}`, "_blank")
  }

  const changeEpisode = (episode) => {
    if (episode.id === actualEpisode?.id) return
    retryCountRef.current = 0
    setRetryKey(0)
    setShowFullDescription(true)
    setHasShortDescription(false)
    setHasChangedEpisode(true)
    setActualEpisode(episode)
    playerRef.current.seekTo(0.0, "fraction")
    setLastPlayedValue(0.0)
  }

  const getFilesize = (size) => {
    const kbSize = size / 1024
    if (kbSize < 1024) {
      return `${kbSize.toFixed(2)} KB`
    }

    return `${(kbSize / 1024).toFixed(2)} MB`
  }

  const skeletonLoader = (
    <>
      <div className="serieMobile__topbar">
        <Skeleton variant="rounded" width={90} height={32} style={{ borderRadius: 20 }} />
      </div>
      <div className="serieMobile__body">
        <div className="serieMobile__video">
          <div className="serieMobile__player">
            <div className="serieMobile__playerContainer">
              <Skeleton variant="rectangular" width="100%" height="100%" sx={{ bgcolor: "#333333", transform: "none" }} />
            </div>
          </div>
          <div className="serieMobile__playlist">
            <Skeleton variant="text" width="60%" height={18} style={{ margin: "12px auto 10px" }} />
            <div className="serieMobile__playlist__content">
              {[1, 2, 3].map((i) => (
                <div key={i} className="serieMobile__playlist__info">
                  <Skeleton variant="rounded" width={80} height={48} style={{ borderRadius: 5, flexShrink: 0 }} />
                  <Skeleton variant="text" width="70%" height={18} />
                </div>
              ))}
            </div>
          </div>
          <div className="serieMobile__info">
            <Skeleton variant="text" width="55%" height={28} />
          </div>
          <Skeleton variant="rounded" width="100%" height={72} style={{ borderRadius: 10 }} />
        </div>
      </div>
    </>
  )

  return (
    <div className="serieMobile">
      <CosmosBackground />
      {isLoading ? (
        skeletonLoader
      ) : (
        <>
        <div className="serieMobile__topbar">
          <button onClick={() => window.history.back()}>
            <div className="episodeScreen__back">
              <ArrowBackIcon /> Voltar
            </div>
          </button>
        </div>
        <div className="serieMobile__body">
          <div className="serieMobile__video">
            <div className="serieMobile__player">
              <div className="serieMobile__playerContainer">
                <ReactPlayer
                  key={`${actualEpisode?.video_hls ?? ""}-${retryKey}`}
                  ref={(player) => (playerRef.current = player)}
                  width="100%"
                  height="100%"
                  playing
                  controls
                  url={actualEpisode?.video_hls}
                  config={{
                    file: {
                      hlsOptions: { autoStartLoad: true, startLevel: -1 },
                    },
                  }}
                  onReady={onPlayerReady}
                  onDuration={onDuration}
                  onProgress={onProgress}
                  onEnded={onEnded}
                  onError={onError}
                />
              </div>
            </div>
            <div className="serieMobile__playlist">
              <h4>{content?.title}</h4>
              <div className="serieMobile__playlist__content">
                {content?.episodes?.map((episode, index) => (
                  <div
                    key={episode.id}
                    className={`${episode.id === actualEpisode?.id ? "serieMobile__playlist__info__active" : "serieMobile__playlist__info"}`}
                    onClick={() => changeEpisode(episode)}
                    ref={
                      episode.id === actualEpisode?.id
                        ? (el) =>
                            el?.scrollIntoView({
                              behavior: "smooth",
                              block: "end",
                            })
                        : null
                    }
                  >
                    <img
                      className={episode.id === actualEpisode?.id ? "serieScreen__playlist__info__thumbnail__active" : "serieScreen__playlist__info__thumbnail"}
                      width={80}
                      height={48}
                      src={encodeURI(`${baseURL}${episode?.thumbnail?.sizes?.card?.url}`)}
                      alt={episode?.thumbnail?.alt || ""}
                    />
                    <h5>{index + 1}. {episode?.title}</h5>
                  </div>
                ))}
              </div>
            </div>
            <div className="serieMobile__info">
              <h1>{getEpisodeIndex(actualEpisode?.id) + 1}. {actualEpisode?.title}</h1>
            </div>
            {description.length > 0 && (
              <div className="serieMobile__description">
                <h4 dangerouslySetInnerHTML={{ __html: limitedDescription }} />
                {!showFullDescription && (
                  <button
                    onClick={() => {
                      setShowFullDescription(true)
                      setHasShortDescription(true)
                    }}
                  >
                    Mostrar Mais
                    <KeyboardArrowDownIcon />
                  </button>
                )}
                {hasShortDescription && (
                  <button
                    onClick={() => {
                      setShowFullDescription(false)
                      setHasShortDescription(false)
                    }}
                  >
                    Mostrar Menos
                    <KeyboardArrowUpIcon />
                  </button>
                )}
              </div>
            )}

            {actualEpisode?.attachment !== null && (
              <div
                className="serieMobile_attachment"
                onClick={() => openAttachment(actualEpisode.attachment.url)}
              >
                <PictureAsPdfIcon />
                <div className="serieMobile_attachment__info">
                  <h5 href={`${baseURL}${actualEpisode?.attachment?.url}`}>
                    {actualEpisode.attachment.filename}
                  </h5>
                  <p>{getFilesize(actualEpisode.attachment.filesize)}</p>
                </div>
              </div>
            )}
          </div>
        </div>
        </>
      )}
    </div>
  )
}

export default SerieMobile
